// Pricing section — 3-column layout (Zakup / Sprzedaż / Marża) + transport
// breakdown.
//
// All money formatting flows through `useFormat().currency()`; FX conversion
// goes through `toPLN()` from `@shared/calc`. Marża + the P&L break-
// down + the FX disclosure are visible only to administracja — logistics +
// sales see the three Pricing boxes (Zakup / Sprzedaż / Transport) without
// the result block.

import React from "react";
import { useTranslation } from "react-i18next";
import type { Transaction, TransactionTransportRow } from "@shared/types/transaction";
import { Tag } from "@ds/display/Tag";
import { Icon } from "@ds/display/Icon";
import { Pill } from "@ds/display/Pill";
import { Button } from "@ds/primitives/Button";
import { EntityLink } from "@ds/connected/EntityLink";
import { Box } from "@ds/layout/Box";
import { Stack } from "@ds/layout/Stack";
import { Inline } from "@ds/layout/Inline";
import { Grid } from "@ds/layout/Grid";
import { TextValue } from "@ds/display/TextValue";
import { Dot } from "@ds/display/Dot";
import { Hairline } from "@ds/display/Hairline";
import { useFormat } from "@ds/display/useNumberFormat";
import { useCurrentEmployee } from "../../../contexts/useCurrentEmployee";
import { useTransactionWorkflow } from "../hooks/transactionWorkflowContext";
import {
  EUR_PLN_RATE, toPLN, effectiveWeight,
  computeMarginPLN, computeTransportCostPLN,
  sideGross, legGross,
} from "@shared/calc";
import { useSubQuery } from "@ds/data/subQueriesContext";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatPriceSuffix = (t: "per_ton" | "flat_rate" | string | null | undefined) =>
  t === "per_ton" ? "/t" : "";

function formatVat(vat?: string | null): string | null {
  if (!vat) return null;
  if (vat === "zw") return "VAT ZW";
  if (vat === "np") return "VAT NP";
  return `VAT ${vat}%`;
}

// ─── PriceColumn — Zakup / Sprzedaż / Marża cell ─────────────────────────────

interface PriceColumnProps {
  eyebrow: string;
  tone: "cost" | "revenue" | "margin";
  value: number | null;
  currency: string;
  priceType?: "per_ton" | "flat_rate" | string | null;
  incoterms?: string | null;
  vat?: string | null;
  source?: string | null;
  weight?: number | null;
  /** Optional sub-line under the margin value (e.g. EUR conversion). */
  marginSubLine?: string;
  /** When set, renders an "Edytuj" link button next to the eyebrow. */
  onEdit?: () => void;
  editLabel?: string;
  /** Optional testid for the edit button (for e2e targeting). */
  editTestId?: string;
  /** Price-list relation — rendered as an inline EntityLink under the value. */
  priceListId?: number | null;
  priceListLabel?: string | null;
}

function PriceColumn({
  eyebrow, tone, value, currency, priceType, incoterms, vat, source, weight, marginSubLine,
  onEdit, editLabel, editTestId, priceListId, priceListLabel,
}: PriceColumnProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const empty = value == null;
  const valueTone: "green" | "red" =
    tone === "margin" ? (value != null && value < 0 ? "red" : "green")
    : tone === "cost"   ? "red"
    : "green";
  const total = !empty && tone !== "margin" && weight != null && priceType === "per_ton"
    ? value * weight
    : !empty && tone !== "margin"
      ? value
      : null;
  // Preserves the original `value! >= 0` coercion exactly: JS evaluates
  // `null >= 0` as true but `undefined >= 0` as false, so the two nullish
  // cases deliberately fall on opposite sides.
  const nonNegative = value !== undefined && (value === null || value >= 0);
  const sign = tone === "margin" ? (nonNegative ? "+" : "−") : "";

  return (
    <Box size="md" border shape="rounded-lg" tone="neutral">
      <Stack gap="xs">
        <Inline align="baseline" justify="between" gap="xs">
          <TextValue size="xs" weight="semibold" uppercase tone="muted">{eyebrow}</TextValue>
          {onEdit && (
            <Button label={editLabel ?? t("transactions.pricing.edit", "Edytuj")} variant="link" size="sm" onClick={onEdit} testId={editTestId} />
          )}
        </Inline>

        {/* Big-amount tabular number; sign inlined into the value. */}
        <TextValue as="div" size="xl" weight="bold" tabular tone={valueTone}>
          {empty ? "—" : (
            tone === "margin"
              ? `${sign}${fmt.currency(Math.abs(value), currency, 2)}`
              : <>
                  {fmt.currency(value, currency, 2)}
                  {priceType ? <TextValue as="span" tone="muted" weight="regular">{` ${formatPriceSuffix(priceType)}`}</TextValue> : null}
                </>
          )}
        </TextValue>

        {/* Per-unit + total CalcLine for cost/revenue columns */}
        {!empty && tone !== "margin" && total != null && (
          <TextValue as="div" size="sm" tone="muted">
            {priceType === "per_ton"
              ? <>Razem: <TextValue as="span" tabular tone="secondary">{fmt.currency(total, currency, 2)}</TextValue></>
              : <TextValue as="span" italic>{t("transactions.pricing.flat_rate_amount", "Kwota ryczałtowa")}</TextValue>}
          </TextValue>
        )}

        {/* Sub-line for margin (e.g. EUR conversion hint) */}
        {tone === "margin" && marginSubLine && (
          <TextValue as="div" size="sm" tone="muted" tabular>{marginSubLine}</TextValue>
        )}

        {/* Incoterms + VAT chips */}
        {(incoterms || vat) && (
          <Inline gap="xs" wrap>
            {incoterms && <Tag label={incoterms} tone="grey" size="sm" />}
            {vat && <Tag label={formatVat(vat)} tone="grey" size="sm" />}
          </Inline>
        )}

        {/* Price-list source / manual indicator (cost+revenue only). */}
        {tone !== "margin" && !empty && (
          priceListId ? (
            <Inline as="span" inline gap="xs" align="center">
              <Dot tone="teal" size="xs" />
              <TextValue size="sm" tone="muted">Cennik:</TextValue>
              <EntityLink
                type="price_list"
                id={priceListId}
                label={priceListLabel ?? `#${priceListId}`}
                variant="inline"
              />
            </Inline>
          ) : source ? (
            <Inline as="span" inline gap="xs" align="center">
              <Dot tone="teal" size="xs" />
              <TextValue size="sm" tone="muted">Cennik:</TextValue>
              <TextValue size="sm">{source}</TextValue>
            </Inline>
          ) : (
            <TextValue as="div" size="sm" tone="faint" italic>{t("transactions.pricing.manual_price", "Cena ręczna")}</TextValue>
          )
        )}
      </Stack>
    </Box>
  );
}


// ─── CalcLine — signed + tone-coded breakdown row inside the margin block ───

interface CalcLineProps {
  label: string;
  role: "income" | "cost";
  convertedPLN: number;
  /** Set when the source amount was in a foreign currency. */
  native?: { value: number; currency: string } | null;
  bold?: boolean;
  isResult?: boolean;
}

function CalcLine({ label, role, convertedPLN, native, bold, isResult }: CalcLineProps) {
  const fmt = useFormat();
  const sign = isResult
    ? "="
    : (role === "income" ? "+" : "−");
  const lineTone: "green" | "red" = isResult
    ? (convertedPLN >= 0 ? "green" : "red")
    : (role === "income" ? "green" : "red");
  return (
    <Inline align="baseline" justify="between">
      <Inline as="span" inline align="baseline" gap="xs">
        <TextValue size="sm" weight={bold ? "semibold" : "regular"} tone={bold ? "primary" : "secondary"}>
          {label}
        </TextValue>
      </Inline>
      <Inline as="span" inline align="baseline" gap="sm">
        {native && (
          <TextValue size="xs" tone="muted" tabular>
            ({fmt.currency(Math.abs(native.value), native.currency, 2)})
          </TextValue>
        )}
        {/* Sign + amount inlined into a single tabular TextValue. */}
        <TextValue
          as="span"
          size="sm"
          weight={bold ? "bold" : "medium"}
          tabular
          tone={lineTone}
        >
          {`${sign} ${fmt.currency(Math.abs(convertedPLN), "PLN", 2)}`}
        </TextValue>
      </Inline>
    </Inline>
  );
}

// ─── TransactionPricing — section root ───────────────────────────────────────

export interface TransactionPricingProps {
  transaction: Transaction;
}

export const TransactionPricing: React.FC<TransactionPricingProps> = ({ transaction: tx }) => {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { hasRole } = useCurrentEmployee();
  const showMargin = hasRole("admin", "developer");
  const { actions } = useTransactionWorkflow();

  const w = effectiveWeight(tx);
  // Transport legs come from the page-batched `transports` sub-query (shared
  // with the Transport + Faktury sections — one fetch for all three).
  const { rows: fetchedLegs } = useSubQuery<TransactionTransportRow>("transports", tx.id);
  const legs: TransactionTransportRow[] = fetchedLegs;
  // Stitch fetched legs onto tx so shared/calc.computeMarginPLN /
  // computeTransportCostPLN see them (both read from tx.transports).
  const txWithLegs = { ...tx, transports: legs } as Transaction;

  // Per-side totals in their native currency.
  const loadTotalNative  = sideGross(tx, 'load');
  const unloadTotalNative = sideGross(tx, 'unload');

  // Per-leg native totals.
  const legNatives = legs.map(l => ({
    leg: l,
    native: legGross(l, w),
  }));

  // PLN conversions (canonical accounting currency).
  const loadPLN   = loadTotalNative   != null ? toPLN(loadTotalNative,   tx.load_price_currency)   : null;
  const unloadPLN = unloadTotalNative != null ? toPLN(unloadTotalNative, tx.unload_price_currency) : null;
  // Canonical transport cost + margin live in `shared/calc.ts` so the FE
  // and the edge function always agree on the formula.
  const transportPLN = computeTransportCostPLN(txWithLegs);
  const transportConversion = legNatives.some(l => l.leg.price_currency !== "PLN");

  const marginCanonical = computeMarginPLN(txWithLegs);
  const hasFullPricing  = marginCanonical != null;
  const marginPLN       = marginCanonical ?? 0;
  const marginPerTon    = w > 0 ? marginPLN / w : marginPLN;

  // Optional price-list source labels — fall back to id-based hint when the
  // relation isn't hydrated (real Transaction type only carries *_id).
  const loadSource = tx.load_price_list_id != null
    ? `#${tx.load_price_list_id}`
    : null;
  const unloadSource = tx.unload_price_list_id != null
    ? `#${tx.unload_price_list_id}`
    : null;

  return (
    // No section Card here — SectionShell provides the card + "Cennik" title.
    <>
      {/* 3-column grid: Załadunek / Rozładunek / Marża. */}
      <Grid columns={3} gap="md">
        <PriceColumn
          eyebrow={t("transactions.pricing.load_side", "Załadunek")}
          tone={(tx.load_price ?? 0) >= 0 ? "revenue" : "cost"}
          value={tx.load_price ?? null}
          currency={tx.load_price_currency || "PLN"}
          priceType={tx.load_price_type}
          incoterms={tx.load_price_incoterms}
          vat={tx.load_price_vat_rate_code}
          source={loadSource}
          weight={tx.loaded_weight}
          onEdit={() => actions.setPrice(tx.id, "load")}
          editTestId="pricing-edit-load"
          priceListId={tx.load_price_list_id ?? null}
        />
        <PriceColumn
          eyebrow={t("transactions.pricing.unload_side", "Rozładunek")}
          tone={(tx.unload_price ?? 0) >= 0 ? "revenue" : "cost"}
          value={tx.unload_price ?? null}
          currency={tx.unload_price_currency || "PLN"}
          priceType={tx.unload_price_type}
          incoterms={tx.unload_price_incoterms}
          vat={tx.unload_price_vat_rate_code}
          source={unloadSource}
          weight={tx.unloaded_weight ?? tx.loaded_weight}
          onEdit={() => actions.setPrice(tx.id, "unload")}
          editTestId="pricing-edit-unload"
          priceListId={tx.unload_price_list_id ?? null}
        />
        {showMargin && (
          <PriceColumn
            eyebrow={t("transactions.pricing.margin", "Marża")}
            tone="margin"
            value={hasFullPricing ? marginPLN : null}
            currency="PLN"
            marginSubLine={hasFullPricing && w > 0
              ? `${marginPerTon >= 0 ? "+" : "−"}${fmt.currency(Math.abs(marginPerTon), "PLN", 2)} / t`
              : undefined}
          />
        )}
      </Grid>

      {/* Marża P&L breakdown — admin-only */}
      {showMargin && hasFullPricing && (
        <Box
          size="md"
          tone={marginPLN >= 0 ? "green" : "red"}
          border
          shape="rounded-xl"
        >
          <Stack gap="md">
            <Inline align="baseline" justify="between" gap="md">
              <Stack gap="xs">
                <TextValue size="xs" weight="semibold" uppercase tone="muted">
                  {t("transactions.pricing.margin_pnl", "Wynik")}
                </TextValue>
                {w > 0 && (
                  <TextValue as="div" size="sm" tone="muted" tabular>
                    <TextValue
                      as="span"
                      size="sm"
                      weight="semibold"
                      tone={marginPerTon >= 0 ? "green" : "red"}
                    >
                      {`${marginPerTon >= 0 ? "+" : "−"}${fmt.currency(Math.abs(marginPerTon), "PLN", 2)}`}
                    </TextValue>
                    <TextValue as="span">{` na tonę `}</TextValue>
                    <TextValue as="span" tone="faint">(× {fmt.weight(w)})</TextValue>
                  </TextValue>
                )}
              </Stack>
              {/* Display-sized tabular margin total; sign inlined. */}
              <TextValue
                as="div"
                size="hero"
                weight="bold"
                tabular
                tone={marginPLN >= 0 ? "green" : "red"}
              >
                {`${marginPLN >= 0 ? "+" : "−"}${fmt.currency(Math.abs(marginPLN), "PLN", 2)}`}
              </TextValue>
            </Inline>

            {/* CalcLine breakdown — surface-clad card. */}
            <Box size="md" border shape="rounded-lg" tone="neutral">
              <Stack gap="xs">
                {loadPLN != null && (
                  <CalcLine
                    label={t("transactions.pricing.load_side", "Załadunek")}
                    role={loadPLN >= 0 ? "income" : "cost"}
                    convertedPLN={loadPLN}
                    native={tx.load_price_currency !== "PLN" && loadTotalNative != null
                      ? { value: loadTotalNative, currency: tx.load_price_currency }
                      : null}
                  />
                )}
                {unloadPLN != null && (
                  <CalcLine
                    label={t("transactions.pricing.unload_side", "Rozładunek")}
                    role={unloadPLN >= 0 ? "income" : "cost"}
                    convertedPLN={unloadPLN}
                    native={tx.unload_price_currency !== "PLN" && unloadTotalNative != null
                      ? { value: unloadTotalNative, currency: tx.unload_price_currency }
                      : null}
                  />
                )}
                <CalcLine
                  label={t("transactions.pricing.transport", "Transport")}
                  role={transportPLN >= 0 ? "income" : "cost"}
                  convertedPLN={transportPLN}
                  native={null}
                />
                <Hairline variant="soft" />
                <CalcLine
                  label={t("transactions.pricing.margin", "Marża")}
                  role={marginPLN >= 0 ? "income" : "cost"}
                  convertedPLN={marginPLN}
                  bold
                  isResult
                />
              </Stack>
            </Box>

            {/* FX disclosure — Pill primitive replaces bespoke chip. */}
            <Inline>
              <Pill tone={transportConversion ? "amber" : "grey"} size="sm">
                <Inline as="span" inline gap="xs" align="center">
                  <Icon name="swapHoriz" size="xs" />
                  <TextValue as="span" size="xs">
                    {t("transactions.pricing.fx_rate", "Kurs walut z dnia transakcji:")}{" "}
                    <TextValue as="span" weight="semibold" tabular>
                      1 EUR = {fmt.fxRate(EUR_PLN_RATE)} PLN
                    </TextValue>
                  </TextValue>
                </Inline>
              </Pill>
            </Inline>
          </Stack>
        </Box>
      )}
    </>
  );
};
